import { DownloadOutlined, SearchOutlined } from '@ant-design/icons'
import { Alert, Button, Drawer, Input, Spin, Switch, Table, Tabs } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { getBilling } from './api'
import { dateOnly, dateTime, dmy, downloadCsv, eur, marginPct, num, pct } from './format'
import { Term } from './Glossary'
import PhoneTimeline, { formatPhone } from './PhoneTimeline'

const Kpi = ({ label, value, color, sub }) => (
  <div style={{ flex: '1 1 150px', border: '1px solid #e5e7eb', borderRadius: 10, padding: '8px 12px' }}>
    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: '#94a3b8' }}>{sub}</div>}
  </div>
)

const money = (v) => eur(v)
const marginCell = (v) => (
  <span style={{ color: v >= 0 ? '#237804' : '#a8071a', fontWeight: 600 }}>{eur(v)}</span>
)
const numCol = (title, dataIndex, extra = {}) => ({
  title,
  dataIndex,
  align: 'right',
  render: num,
  sorter: (a, b) => a[dataIndex] - b[dataIndex],
  ...extra
})

/** Detalle de un cliente en el periodo: por teléfono y por campaña. */
const UserDetailDrawer = ({ user, range, onClose }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [search, setSearch] = useState('')
  const [onlyLoss, setOnlyLoss] = useState(false)
  const [phone, setPhone] = useState(null)

  useEffect(() => {
    if (!user) return undefined
    let active = true
    setLoading(true)
    setError(false)
    setData(null)
    setSearch('')
    setOnlyLoss(false)
    getBilling({ userId: user.userId, from: range.from, to: range.to, view: 'detail' })
      .then((res) => active && setData(res))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [user, range])

  // Filas compactas (columns/rows) -> objetos
  const phones = useMemo(() => {
    if (!data || !data.phones) return []
    const cols = data.phones.columns
    return data.phones.rows.map((r) => {
      const o = {}
      cols.forEach((c, i) => {
        o[c] = r[i]
      })
      o.margin = o.retail - o.cost
      return o
    })
  }, [data])

  // Referencia estable: PhoneTimeline recarga cuando cambia.
  const campaigns = useMemo(() => (data && data.campaigns) || [], [data])

  // El backend da los recuentos sobre TODOS los teléfonos aunque recorte la lista.
  const phoneMeta = (data && data.phones) || {}
  const phonesTotal = phoneMeta.total != null ? phoneMeta.total : phones.length
  const lossCount = useMemo(
    () => (phoneMeta.loss != null ? phoneMeta.loss : phones.filter((p) => p.margin < 0).length),
    [phones, phoneMeta.loss]
  )

  const filtered = useMemo(() => {
    const q = search.replace(/\D/g, '')
    return phones.filter(
      (p) => (!q || String(p.phone).includes(q)) && (!onlyLoss || p.margin < 0)
    )
  }, [phones, search, onlyLoss])

  const exportCsv = () => {
    downloadCsv(
      `coste-telefonos-${user.name}-${range.from}_${range.to}.csv`,
      ['Teléfono', 'Campañas', 'Enviados', 'Entregados', 'Vistos', 'Fallidos', 'Conversación', 'Sesión extra', 'Simple', 'Básico', 'Incluidos', 'Coste Orange', 'Cobrado', 'Margen', 'Último envío'],
      filtered.map((p) => [
        p.phone,
        p.campaigns.length,
        p.sent,
        p.delivered,
        p.displayed,
        p.failed,
        p.conversacion,
        p.extra,
        p.simple,
        p.basico,
        p.bundled,
        p.cost.toFixed(3).replace('.', ','),
        p.retail.toFixed(3).replace('.', ','),
        p.margin.toFixed(3).replace('.', ','),
        p.lastSent
      ])
    )
  }

  const phoneColumns = [
    {
      title: 'Teléfono',
      dataIndex: 'phone',
      fixed: 'left',
      render: (v) => <span style={{ color: '#4F46E5', whiteSpace: 'nowrap' }}>{formatPhone(v)}</span>
    },
    numCol('Campañas', 'campaigns', {
      render: (v) => v.length,
      sorter: (a, b) => a.campaigns.length - b.campaigns.length
    }),
    numCol('Enviados', 'sent'),
    numCol('Entregados', 'delivered'),
    numCol('Vistos', 'displayed'),
    numCol(<Term id="conversacion" />, 'conversacion'),
    numCol(<Term id="extra" />, 'extra'),
    numCol(<Term id="simple" />, 'simple'),
    numCol(<Term id="basico" />, 'basico'),
    numCol(<Term id="incluido" />, 'bundled'),
    {
      title: 'Coste',
      dataIndex: 'cost',
      align: 'right',
      render: (v) => eur(v),
      sorter: (a, b) => a.cost - b.cost,
      defaultSortOrder: 'descend'
    },
    {
      title: 'Cobrado',
      dataIndex: 'retail',
      align: 'right',
      render: money,
      sorter: (a, b) => a.retail - b.retail
    },
    {
      title: 'Margen',
      dataIndex: 'margin',
      align: 'right',
      render: marginCell,
      sorter: (a, b) => a.margin - b.margin
    },
    {
      title: 'Último envío',
      dataIndex: 'lastSent',
      render: dateTime,
      sorter: (a, b) => (a.lastSent < b.lastSent ? -1 : 1)
    }
  ]

  const campaignColumns = [
    {
      title: 'Fecha',
      dataIndex: 'timestamp',
      render: dateOnly,
      sorter: (a, b) => (a.timestamp < b.timestamp ? -1 : 1),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Campaña',
      dataIndex: 'label',
      ellipsis: true,
      render: (v, c) => <span title={c.campaignId}>{v}</span>
    },
    { title: 'Estado', dataIndex: 'status', render: (v) => (v === 'open' ? 'Abierta' : 'Cerrada') },
    numCol(<Term id="contactados">Teléfonos</Term>, 'contacted'),
    numCol('Enviados', 'sent'),
    {
      title: 'Entregados',
      key: 'deliveredPct',
      align: 'right',
      render: (_, c) => pct(c.delivered, c.sent)
    },
    numCol('Interactuaron', 'interactedPhones'),
    numCol(<Term id="conversacion" />, 'firstSessions'),
    numCol(<Term id="extra" />, 'extraSessions'),
    numCol(<Term id="simple" />, 'simple'),
    numCol(<Term id="incluido" />, 'bundled'),
    { title: 'Coste', dataIndex: 'cost', align: 'right', render: money, sorter: (a, b) => a.cost - b.cost },
    { title: 'Cobrado', dataIndex: 'retailRcs', align: 'right', render: money },
    { title: 'Margen', dataIndex: 'margin', align: 'right', render: marginCell, sorter: (a, b) => a.margin - b.margin }
  ]

  const contacted = data && data.phoneFunnel ? data.phoneFunnel.contacted : 0

  return (
    <Drawer
      title={user ? `${user.name} · ${dmy(range.from)} – ${dmy(range.to)}` : ''}
      open={!!user}
      onClose={onClose}
      width="min(1280px, calc(100vw - 24px))"
      destroyOnClose
    >
      <Spin spinning={loading} tip="Calculando el detalle por teléfono…">
        <div style={{ minHeight: loading && !data ? 300 : undefined }}>
          {error && <Alert type="error" showIcon message="No se pudo cargar el detalle" />}
          {data && (
            <>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                <Kpi label="Coste Orange" value={eur(data.cost)} color="#b45309" />
                <Kpi label="Cobrado al cliente" value={eur(data.retailRcs)} />
                <Kpi
                  label={`Margen (${marginPct(data.margin, data.retailRcs)})`}
                  value={eur(data.margin)}
                  color={data.margin >= 0 ? '#237804' : '#a8071a'}
                />
                <Kpi
                  label="Teléfonos contactados"
                  value={num(contacted)}
                  sub={`Coste medio ${eur(contacted ? data.cost / contacted : 0, 3)} por teléfono`}
                />
                <Kpi
                  label="Teléfonos en pérdida"
                  value={num(lossCount)}
                  color={lossCount ? '#a8071a' : undefined}
                  sub="Cuestan más de lo que se cobra"
                />
              </div>
              <Tabs
                items={[
                  {
                    key: 'phones',
                    label: `Por teléfono (${num(phonesTotal)})`,
                    children: (
                      <>
                        {phoneMeta.truncated && (
                          <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message={`Periodo muy largo: se listan los ${num(phones.length)} teléfonos de mayor coste de ${num(phonesTotal)}.`}
                            description="Los totales y los teléfonos en pérdida sí cuentan todos. Para ver, buscar o exportar la lista completa, acota el periodo a un mes."
                          />
                        )}
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                          <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            placeholder="Buscar número"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ width: 220 }}
                          />
                          <span style={{ fontSize: 13 }}>
                            <Switch size="small" checked={onlyLoss} onChange={setOnlyLoss} /> Solo en pérdida
                          </span>
                          <span style={{ flex: 1 }} />
                          <Button icon={<DownloadOutlined />} onClick={exportCsv} disabled={!filtered.length}>
                            Exportar CSV
                          </Button>
                        </div>
                        <Table
                          rowKey="phone"
                          size="small"
                          columns={phoneColumns}
                          dataSource={filtered}
                          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (n) => `${num(n)} teléfonos` }}
                          scroll={{ x: 1300 }}
                          onRow={(p) => ({ onClick: () => setPhone(p), style: { cursor: 'pointer' } })}
                        />
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>
                          Pulsa un teléfono para ver sus mensajes y cuánto ha costado su conversación.
                        </div>
                      </>
                    )
                  },
                  {
                    key: 'campaigns',
                    label: `Por campaña (${num(campaigns.length)})`,
                    children: (
                      <Table
                        rowKey="campaignId"
                        size="small"
                        columns={campaignColumns}
                        dataSource={campaigns}
                        pagination={{ pageSize: 25 }}
                        scroll={{ x: 1300 }}
                      />
                    )
                  }
                ]}
              />
            </>
          )}
        </div>
      </Spin>
      {data && (
        <PhoneTimeline
          userId={user.userId}
          phoneRow={phone}
          campaigns={campaigns}
          range={range}
          onClose={() => setPhone(null)}
        />
      )}
    </Drawer>
  )
}

export default UserDetailDrawer
